import { MedicalReviewState } from "@/constants/Onboarding";
import { FAIconName } from "@/ui/core";
import type { ButtonVariant } from "@/ui/core/buttons/Button";

interface MedicalReviewButton {
  leftIcon?: FAIconName;
  rightIcon?: FAIconName;
  title: string;
  variant: ButtonVariant;
}

interface MedicalReviewContent {
  button: MedicalReviewButton;
  caption: string;
  description: string;
  estimate?: string;
  iconName: FAIconName;
  title: string;
}

interface MedicalReviewDynamicContent {
  description: string;
  title: string;
}

type MedicalReviewDynamicContentBuilder = (
  patientName?: string,
) => MedicalReviewDynamicContent;

const MedicalReviewDynamicContentRecord: Record<
  MedicalReviewState,
  MedicalReviewDynamicContentBuilder
> = {
  [MedicalReviewState.Approved]: (patientName) => {
    const whose = patientName ? `${patientName}'s` : "your";
    const pronoun = patientName ? "them" : "you";

    return {
      description: `Our provider has reviewed ${whose} clinical picture and **Equip is the right fit for ${pronoun}.**`,
      title: "Continue onboarding",
    };
  },
  [MedicalReviewState.NotApproved]: (patientName) => {
    const forWhom = patientName ? `for ${patientName} ` : "";

    return {
      description: `Our provider found that **Equip may not be the right fit ${forWhom}right now.** Someone from our team will reach out shortly to talk through your options.`,
      title: patientName
        ? `Let's find the right care for ${patientName}`
        : "Let's find the right care for you",
    };
  },
  [MedicalReviewState.Pending]: (patientName) => {
    const whose = patientName ? `${patientName}'s` : "your";

    return {
      description: `A provider is reading ${whose} clinical picture to confirm Equip is the right fit. **While you wait, our full library of videos and articles is yours to browse.** Here's a video on the Equip care model to get started.`,
      title: "Medical review",
    };
  },
};

interface MedicalReviewStaticContent {
  button: MedicalReviewButton;
  caption: string;
  estimate?: string;
  iconName: FAIconName;
}

const MedicalReviewStaticContentRecord: Record<
  MedicalReviewState,
  MedicalReviewStaticContent
> = {
  [MedicalReviewState.Approved]: {
    button: { leftIcon: FAIconName.Check, title: "Got it", variant: "primary" },
    caption: "Review complete",
    iconName: FAIconName.Check,
  },
  [MedicalReviewState.NotApproved]: {
    button: {
      rightIcon: FAIconName.ArrowRight,
      title: "Learn more",
      variant: "tertiary",
    },
    caption: "Review complete",
    iconName: FAIconName.InfoCircle,
  },
  [MedicalReviewState.Pending]: {
    button: {
      rightIcon: FAIconName.ArrowRight,
      title: "Explore more in the app",
      variant: "tertiary",
    },
    caption: "Pending",
    estimate: "1-2 business days",
    iconName: FAIconName.AlarmClock,
  },
};

interface GetMedicalReviewContentParams {
  patientName?: string;
  state: MedicalReviewState;
}

const getMedicalReviewContent = ({
  patientName,
  state,
}: GetMedicalReviewContentParams): MedicalReviewContent => {
  const staticContent = MedicalReviewStaticContentRecord[state];
  const dynamicContent = MedicalReviewDynamicContentRecord[state](patientName);

  return {
    button: staticContent.button,
    caption: staticContent.caption,
    description: dynamicContent.description,
    estimate: staticContent.estimate,
    iconName: staticContent.iconName,
    title: dynamicContent.title,
  };
};

export { getMedicalReviewContent };
